import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Announcement, CreateAnnouncementInput, EAnnouncementAppType } from '@insolution/graphql-data-access';
import { MultiLanguageDatepicker } from '@insolution/language-feature';
import { TranslatePipe } from '@ngx-translate/core';
import { IAnnouncementFormControls } from './interfaces/announcement-form.interface';

@Component({
  selector: 'lib-announcement-form',
  imports: [ReactiveFormsModule, TranslatePipe, MultiLanguageDatepicker],
  templateUrl: './announcement-form.html',
  styleUrl: './announcement-form.scss',
})
export class AnnouncementForm {
  public announcement = input<Announcement | undefined>();
  public isLoading = input(false);
  public announcementSubmit = output<CreateAnnouncementInput>();
  public cancelForm = output<void>();

  private formBuilder = inject(FormBuilder);

  protected submitted = signal(false);
  protected appTypeOptions = Object.values(EAnnouncementAppType);

  protected formGroup = this.formBuilder.group<IAnnouncementFormControls>({
    id: this.formBuilder.control(null),
    title: this.formBuilder.control(null, [Validators.required]),
    appType: this.formBuilder.control<EAnnouncementAppType | null>(null, [Validators.required]),
    nl_NL: this.formBuilder.control(null, [Validators.required]),
    de_DE: this.formBuilder.control(null, [Validators.required]),
    en_US: this.formBuilder.control(null, [Validators.required]),
    fr_FR: this.formBuilder.control(null, [Validators.required]),
    fi_FI: this.formBuilder.control(null, [Validators.required]),
    start_date: this.formBuilder.control(null, [Validators.required]),
    end_date: this.formBuilder.control(null, [Validators.required]),
  });

  constructor() {
    effect(() => {
      const announcement = this.announcement();

      untracked(() => {
        this.submitted.set(false);

        if (announcement) {
          this.patchForm(announcement);
        } else {
          this.formGroup.reset();
          this.formGroup.controls.id.setValue(crypto.randomUUID());
        }
      });
    });
  }

  protected onSubmit(): void {
    this.submitted.set(true);

    if (this.formGroup.invalid) {
      return;
    }

    const value = this.formGroup.getRawValue();

    this.announcementSubmit.emit({
      id: value.id!,
      title: value.title!,
      appType: value.appType!,
      messages: {
        nl_NL: value.nl_NL!,
        de_DE: value.de_DE!,
        en_US: value.en_US!,
        fr_FR: value.fr_FR!,
        fi_FI: value.fi_FI!,
      },
      start_date: value.start_date!,
      end_date: value.end_date!,
    });
  }

  protected onCancel(): void {
    this.cancelForm.emit();
  }

  private patchForm(announcement: Announcement): void {
    this.formGroup.patchValue({
      id: announcement.id,
      title: announcement.title,
      appType: announcement.appType,
      nl_NL: announcement.messages.nl_NL,
      de_DE: announcement.messages.de_DE,
      en_US: announcement.messages.en_US,
      fr_FR: announcement.messages.fr_FR,
      fi_FI: announcement.messages.fi_FI,
      start_date: announcement.start_date,
      end_date: announcement.end_date,
    });
  }
}
